/**
 * Census2022SummaryCard — Resumen censal 2022 del proyecto
 *
 * @remarks
 * Muestra siempre: grilla compacta de 6 métricas clave.
 * Sección extendida collapsible con educación, salud, vivienda, cobertura y metadatos.
 * Maneja todos los estados: none / processing_2022 / ready_2022 / stale_2022 / error_2022
 */

import { useEffect, useState, useCallback } from 'react';
import { ChevronDown, ChevronUp, RefreshCw, RotateCcw } from 'lucide-react';
import {
  census2022Api,
  type Census2022Status,
  type CensusSummary2022,
} from './census-2022-api';
import { Census2022StatusBadge } from './Census2022StatusBadge';
import { Census2022SlicesPreview } from './Census2022SlicesPreview';

interface Census2022SummaryCardProps {
  projectId: string;
  onStatusChange?: (status: Census2022Status) => void;
}

// ─── Formateadores ────────────────────────────────────────────────────────────

function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return Math.round(n).toLocaleString('es-AR');
}

function fmtPctDirect(n: number | null | undefined, dec = 1): string {
  if (n === null || n === undefined) return '—';
  return `${n.toFixed(dec)}%`;
}

function fmtPct(n: number | null | undefined, dec = 1): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(dec)}%`;
}

function fmtKm2(m2: number | null | undefined): string {
  if (m2 === null || m2 === undefined) return '—';
  const km2 = m2 / 1_000_000;
  return `${km2.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km²`;
}

function fmtDate(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtAreaSource(src: string | null | undefined): string {
  if (!src) return '—';
  return src === 'custom_bounds' ? 'Polígono propio' : 'Bounding box';
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }): JSX.Element {
  return (
    <div className="c22-row">
      <span className="c22-label">{label}</span>
      <span className={`c22-value${accent ? ' c22-value--accent' : ''}`}>{value}</span>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="c22-section-title">{children}</div>;
}

function Divider(): JSX.Element {
  return <div className="c22-divider" />;
}

interface MetricCellProps {
  label: string;
  value: string;
  accent?: boolean;
}

function MetricCell({ label, value, accent }: MetricCellProps): JSX.Element {
  return (
    <div className="c22-metric">
      <span className={`c22-metric-value${accent ? ' c22-metric-value--accent' : ''}`}>{value}</span>
      <span className="c22-metric-label">{label}</span>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function Census2022SummaryCard({ projectId, onStatusChange }: Census2022SummaryCardProps): JSX.Element {
  const [status,        setStatus]        = useState<Census2022Status>('none');
  const [summary,       setSummary]       = useState<CensusSummary2022 | null>(null);
  const [errorMsg,      setErrorMsg]      = useState<string | null>(null);
  const [loading,       setLoading]       = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg,     setActionMsg]     = useState<string | null>(null);
  const [showDetail,    setShowDetail]    = useState(false);
  const [showPartials,  setShowPartials]  = useState(false);

  // ── Carga inicial ──────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setErrorMsg(null);

    try {
      const statusRes = await census2022Api.getStatus(projectId);
      const st = statusRes.census2022Status as Census2022Status;
      setStatus(st);
      onStatusChange?.(st);

      if (st === 'ready_2022' || st === 'stale_2022') {
        const sumRes = await census2022Api.getSummary(projectId);
        setSummary(sumRes);
      } else {
        setSummary(null);
      }
    } catch {
      setErrorMsg('Error al cargar estado censal');
    } finally {
      setLoading(false);
    }
  }, [projectId, onStatusChange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Acciones ───────────────────────────────────────────────────────────────

  const handlePrepare = async (): Promise<void> => {
    setActionLoading(true);
    setActionMsg(null);
    try {
      await census2022Api.prepare(projectId);
      setActionMsg('Recorte censal preparado correctamente.');
      await loadData();
    } catch {
      setActionMsg('Error al preparar el recorte censal.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRecalculate = async (): Promise<void> => {
    setActionLoading(true);
    setActionMsg(null);
    try {
      await census2022Api.recalculate(projectId);
      setActionMsg('Recorte censal recalculado correctamente.');
      await loadData();
    } catch {
      setActionMsg('Error al recalcular el recorte censal.');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Render: loading ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="c22-card c22-card--loading">
        Cargando datos censales 2022…
      </div>
    );
  }

  // ── Render: error de carga ─────────────────────────────────────────────────

  if (errorMsg && !summary) {
    return (
      <div className="c22-card c22-card--error">
        <div className="c22-card-header">
          <span className="c22-card-title">Censo 2022</span>
          <Census2022StatusBadge status="error_2022" />
        </div>
        <div className="c22-error-msg">{errorMsg}</div>
        <button className="c22-btn c22-btn--secondary" onClick={loadData}>
          Reintentar
        </button>
      </div>
    );
  }

  // ── Render: none ───────────────────────────────────────────────────────────

  if (status === 'none') {
    return (
      <div className="c22-card">
        <div className="c22-card-header">
          <span className="c22-card-title">Censo 2022</span>
          <Census2022StatusBadge status="none" />
        </div>
        <div className="c22-empty-msg">
          Datos censales 2022 aún no preparados para este proyecto.
        </div>
        {actionMsg && <div className="c22-action-msg">{actionMsg}</div>}
        <button
          className="c22-btn c22-btn--primary"
          onClick={handlePrepare}
          disabled={actionLoading}
        >
          {actionLoading ? 'Preparando…' : 'Preparar censo 2022'}
        </button>
      </div>
    );
  }

  // ── Render: processing ─────────────────────────────────────────────────────

  if (status === 'processing_2022') {
    return (
      <div className="c22-card">
        <div className="c22-card-header">
          <span className="c22-card-title">Censo 2022</span>
          <Census2022StatusBadge status="processing_2022" />
        </div>
        <div className="c22-empty-msg c22-empty-msg--processing">
          Procesando recorte censal…
        </div>
        <button className="c22-btn c22-btn--secondary" onClick={loadData} disabled>
          Actualizar
        </button>
      </div>
    );
  }

  // ── Render: error_2022 ─────────────────────────────────────────────────────

  if (status === 'error_2022') {
    return (
      <div className="c22-card c22-card--error">
        <div className="c22-card-header">
          <span className="c22-card-title">Censo 2022</span>
          <Census2022StatusBadge status="error_2022" />
        </div>
        <div className="c22-error-msg">
          Ocurrió un error al preparar el recorte censal.
        </div>
        {actionMsg && <div className="c22-action-msg">{actionMsg}</div>}
        <button
          className="c22-btn c22-btn--primary"
          onClick={handlePrepare}
          disabled={actionLoading}
        >
          {actionLoading ? 'Reintentando…' : 'Reintentar'}
        </button>
      </div>
    );
  }

  // ── Render: ready_2022 / stale_2022 ───────────────────────────────────────

  const s           = summary;
  const isStale     = status === 'stale_2022';
  const partialCount = s?.partialRadios ?? 0;

  return (
    <div className={`c22-card${isStale ? ' c22-card--stale' : ''}`}>

      {/* Header */}
      <div className="c22-card-header">
        <span className="c22-card-title">Censo 2022</span>
        <Census2022StatusBadge status={status} />
      </div>

      {/* Aviso stale */}
      {isStale && (
        <div className="c22-stale-notice">
          El área cambió. Conviene recalcular el recorte censal.
        </div>
      )}

      {/* Grilla compacta de métricas clave */}
      <div className="c22-compact-grid">
        <MetricCell label="Población"  value={fmt(s?.populationTotal)} accent />
        <MetricCell label="Hogares"    value={fmt(s?.householdsTotal)} />
        <MetricCell label="NBI"        value={fmtPctDirect(s?.pctNbi)} />
        <MetricCell label="Radios"     value={fmt(s?.totalRadios)} />
        <MetricCell label="Cobertura"  value={fmtPct(s?.coverageAgainstProjectArea)} />
        <MetricCell label="Parciales"  value={fmt(s?.partialRadios)} />
      </div>

      {/* Toggle de detalle */}
      <button
        className="c22-detail-toggle"
        onClick={() => setShowDetail((v) => !v)}
      >
        {showDetail ? 'Ocultar detalle' : 'Ver detalle'}
        {showDetail ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>

      {/* Sección extendida collapsible */}
      {showDetail && (
        <>
          <Divider />

          <SectionTitle>Población</SectionTitle>
          <Row label="Población total"      value={fmt(s?.populationTotal)} accent />
          <Row label="En hogares"           value={fmt(s?.populationTotalHh)} />
          <Row label="18 años o más"        value={fmt(s?.pop18Plus)} />

          <Divider />

          <SectionTitle>Hogares</SectionTitle>
          <Row label="Hogares totales"      value={fmt(s?.householdsTotal)} accent />
          <Row label="Con NBI"              value={fmt(s?.householdsWithNbi)} />
          <Row label="% NBI"                value={fmtPctDirect(s?.pctNbi)} />

          <Divider />

          <SectionTitle>Educación y salud</SectionTitle>
          <Row label="Universitario completo" value={fmt(s?.eduUniversityComplete)} />
          <Row label="Secundaria o más"       value={fmt(s?.eduSecondaryCompletePlus)} />
          <Row label="% Universitario (18+)"  value={fmtPctDirect(s?.pctUniversity)} />
          <Row label="Solo salud pública"     value={fmt(s?.healthPublicOnly)} />
          <Row label="% Solo salud pública"   value={fmtPctDirect(s?.pctHealthPublicOnly)} />

          <Divider />

          <SectionTitle>Vivienda</SectionTitle>
          <Row label="Sin cloaca"             value={fmt(s?.householdsNoSewer)} />
          <Row label="Hacinamiento crítico"   value={fmt(s?.householdsOvercrowdingCritical)} />
          <Row label="% Sin cloaca"           value={fmtPctDirect(s?.pctNoSewer)} />
          <Row label="% Hacinam. crítico"     value={fmtPctDirect(s?.pctOvercrowdingCritical)} />

          <Divider />

          <SectionTitle>Cobertura</SectionTitle>
          <Row label="Radios totales"         value={fmt(s?.totalRadios)} />
          <Row label="Radios completos"       value={fmt(s?.fullRadios)} />
          <Row label="Radios parciales"       value={fmt(s?.partialRadios)} />
          <Row label="Sin indicadores"        value={fmt(s?.radiosWithoutIndicatorsCount)} />
          <Row label="Cobertura del área"     value={fmtPct(s?.coverageAgainstProjectArea)} />
          <Row label="Área del proyecto"      value={fmtKm2(s?.projectAreaM2)} />
          <Row label="Fuente del área"        value={fmtAreaSource(s?.areaSource)} />
          <Row label="Método"                 value={s?.method ?? '—'} />
          <Row label="Fecha de cálculo"       value={fmtDate(s?.updatedAt)} />

          {/* Preview de parciales */}
          {partialCount > 0 && (
            <>
              <Divider />
              <button
                className="c22-toggle-partials"
                onClick={() => setShowPartials((v) => !v)}
              >
                {showPartials ? 'Ocultar radios parciales' : `Ver ${partialCount} radios parciales`}
              </button>
              {showPartials && <Census2022SlicesPreview projectId={projectId} />}
            </>
          )}
        </>
      )}

      {/* Mensaje de acción */}
      {actionMsg && (
        <div className={`c22-action-msg${actionMsg.startsWith('Error') ? ' c22-action-msg--error' : ''}`}>
          {actionMsg}
        </div>
      )}

      {/* Nota metodológica */}
      <div className="c22-method-note">
        Los valores se calculan a partir del recorte del área del proyecto sobre la base censal nacional 2022.
      </div>

      {/* Acciones — layout diferente para ready vs stale */}
      <Divider />
      {isStale ? (
        /* stale: recalcular es la acción principal */
        <div className="c22-actions">
          <button
            className="c22-btn c22-btn--secondary"
            onClick={loadData}
            disabled={actionLoading}
            title="Recargar sin recalcular"
          >
            <RefreshCw size={11} />
            Actualizar vista
          </button>
          <button
            className="c22-btn c22-btn--primary"
            onClick={handleRecalculate}
            disabled={actionLoading}
            title="Recalcular el recorte sobre el nuevo área"
          >
            <RotateCcw size={11} />
            {actionLoading ? 'Recalculando…' : 'Recalcular recorte'}
          </button>
        </div>
      ) : (
        /* ready: datos ok, recalcular es secundario */
        <div className="c22-actions c22-actions--ready">
          <button
            className="c22-btn c22-btn--ghost"
            onClick={loadData}
            disabled={actionLoading}
            title="Recargar sin recalcular"
          >
            <RefreshCw size={10} />
            Actualizar vista
          </button>
          <button
            className="c22-btn c22-btn--ghost c22-btn--dimmed"
            onClick={handleRecalculate}
            disabled={actionLoading}
            title="Forzar recálculo completo del recorte (no necesario si los datos están listos)"
          >
            <RotateCcw size={10} />
            {actionLoading ? 'Recalculando…' : 'Recalcular recorte'}
          </button>
        </div>
      )}

    </div>
  );
}
